<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Head, Link, usePage } from '@inertiajs/vue3';
import { Moon, Sun } from 'lucide-vue-next';
import { home, dashboard, login, register } from '@/routes';
import { useAppearance } from '@/composables/useAppearance';
import { useLocale } from '@/composables/useLocale';
import Wcag22AaBadge from '@/components/Wcag22AaBadge.vue';
import CookieConsentBanner from '@/components/CookieConsentBanner.vue';
import AppLogoIcon from '@/components/AppLogoIcon.vue';
import type { Auth } from '@/types';

const { t } = useI18n();
const { locale, locales, isAppDomain, urlForLocale, appUrl, currentPath } = useLocale();
const { resolvedAppearance, updateAppearance } = useAppearance();

function toggleTheme() {
    updateAppearance(resolvedAppearance.value === 'dark' ? 'light' : 'dark');
}

const page = usePage<{ auth: Auth; canRegister: boolean; localeSlugs: Record<string, string>; landingPages: Array<{ slug: string; title: string }>; availableLocales?: string[] }>();
const isAuthenticated = computed(() => !!page.props.auth?.user);
const canRegister = computed(() => page.props.canRegister !== false);
const visibleLocales = computed(() => {
    const available = page.props.availableLocales;
    if (available) {
        return locales.value.filter((l: string) => available.includes(l));
    }
    return locales.value;
});
const mobileMenuOpen = ref(false);

function slug(routeName: string): string {
    return '/' + ((page.props.localeSlugs as Record<string, string>)?.[routeName] || routeName);
}

const navLinks = computed(() => [
    { key: 'layout.nav.howItWorks', href: slug('how-it-works') },
    { key: 'layout.nav.paymentProviders', href: slug('payment-providers') },
    { key: 'layout.nav.forCreators', href: slug('for-creators') },
    { key: 'layout.nav.pricing', href: slug('pricing') },
]);

const footerCols = computed(() => [
    { hKey: 'layout.footer.columns.product.heading', l: [
        { tKey: 'layout.footer.columns.product.howItWorks', href: slug('how-it-works') },
        { tKey: 'layout.footer.columns.product.pricing', href: slug('pricing') },
        { tKey: 'layout.footer.columns.product.forCreators', href: slug('for-creators') },
        { tKey: 'layout.footer.columns.product.forSupporters', href: slug('for-supporters') },
        { tKey: 'layout.footer.columns.product.cancel', href: '/app/member/cancel' },
    ]},
    { hKey: 'layout.footer.columns.company.heading', l: [
        { tKey: 'layout.footer.columns.company.about', href: slug('about') },
        { tKey: 'layout.footer.columns.company.blog', href: slug('blog') },
        { tKey: 'layout.footer.columns.company.contact', href: slug('contact') },
        { tKey: 'layout.footer.columns.company.press', href: slug('press') },
        { tKey: 'layout.footer.columns.company.careers', href: slug('careers') },
    ]},
    { hKey: 'layout.footer.columns.legal.heading', l: [
        { tKey: 'layout.footer.columns.legal.terms', href: slug('terms') },
        { tKey: 'layout.footer.columns.legal.privacy', href: slug('privacy') },
        { tKey: 'layout.footer.columns.legal.cookies', href: slug('cookies') },
        { tKey: 'layout.footer.columns.legal.dataProcessing', href: slug('data-processing') },
        { tKey: 'layout.footer.columns.legal.complaints', href: slug('complaints') },
    ]},
    { hKey: 'layout.footer.columns.support.heading', l: [
        { tKey: 'layout.footer.columns.support.helpCenter', href: 'https://help.novantt.com', external: true },
        { tKey: 'layout.footer.columns.support.status', href: 'https://status.novantt.com', external: true },
        { tKey: 'layout.footer.columns.support.paymentProviders', href: slug('payment-providers') },
        { tKey: 'layout.footer.columns.support.apiDocs', href: 'https://docs.novantt.com', external: true },
        { tKey: 'layout.footer.columns.support.security', href: slug('security') },
    ]},
]);
</script>

<template>
    <Head />


    <div class="bg-background text-foreground font-geist antialiased min-h-svh flex flex-col">
        <!-- Skip to main content -->
        <a href="#main-content" class="skip-link">{{ t('layout.a11y.skipToContent') }}</a>

        <!-- Nav -->
        <header class="h-16 border-b-[0.5px] border-border bg-background flex items-center px-5 gap-4 relative z-5 lg:px-14 lg:gap-8">
            <Link :href="home()" class="inline-flex items-center gap-2 font-semibold text-[19px] tracking-[-0.5px] leading-none shrink-0 no-underline text-inherit">
                <AppLogoIcon class="w-[22px] h-[22px]" aria-hidden="true" />
                Novantt
            </Link>
            <nav class="hidden lg:flex lg:gap-7 lg:text-sm lg:text-muted-foreground" :aria-label="t('layout.a11y.mainNav')">
                <Link v-for="link in navLinks" :key="link.key" :href="link.href" class="text-inherit no-underline">{{ t(link.key) }}</Link>
            </nav>
            <div class="ml-auto flex items-center gap-2.5 text-[13px] lg:gap-3.5">
                <div v-if="visibleLocales.length > 1" role="group" :aria-label="t('layout.a11y.languageSwitcher')" class="hidden lg:flex border-[0.5px] border-border rounded-full p-0.5">
                    <a
                        v-for="loc in visibleLocales"
                        :key="loc"
                        :href="urlForLocale(currentPath(), loc)"
                        :aria-current="locale === loc ? 'true' : undefined"
                        :lang="loc"
                        class="rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-[0.6px] uppercase no-underline"
                        :class="locale === loc ? 'bg-foreground text-background' : 'bg-transparent text-muted-foreground'"
                    >{{ loc.toUpperCase() }}</a>
                </div>
                <button
                    class="flex items-center justify-center size-8 rounded-lg border-[0.5px] border-border bg-transparent text-muted-foreground hover:text-foreground cursor-pointer"
                    @click="toggleTheme"
                    :aria-label="resolvedAppearance === 'dark' ? t('layout.a11y.switchToLight') : t('layout.a11y.switchToDark')"
                >
                    <Sun v-if="resolvedAppearance === 'dark'" class="size-4" :stroke-width="1.6" aria-hidden="true" />
                    <Moon v-else class="size-4" :stroke-width="1.6" aria-hidden="true" />
                </button>
                <template v-if="isAuthenticated">
                    <Link v-if="isAppDomain" :href="dashboard()" class="text-muted-foreground no-underline">{{ t('layout.auth.dashboard') }}</Link>
                    <a v-else :href="appUrl(dashboard())" class="text-muted-foreground no-underline">{{ t('layout.auth.dashboard') }}</a>
                </template>
                <template v-else>
                    <Link v-if="isAppDomain" :href="login()" class="text-muted-foreground no-underline hidden lg:inline">{{ t('layout.auth.login') }}</Link>
                    <a v-else :href="appUrl(login())" class="text-muted-foreground no-underline hidden lg:inline">{{ t('layout.auth.login') }}</a>
                    <Link v-if="canRegister && isAppDomain" :href="register()" class="bg-foreground text-background py-2 px-3.5 rounded-lg no-underline font-medium">{{ t('layout.auth.becomeCreator') }}</Link>
                    <a v-else-if="canRegister" :href="appUrl(register())" class="bg-foreground text-background py-2 px-3.5 rounded-lg no-underline font-medium">{{ t('layout.auth.becomeCreator') }}</a>
                </template>
            </div>
            <!-- Mobile hamburger -->
            <button
                class="flex lg:hidden bg-none border-none cursor-pointer p-1 text-foreground"
                @click="mobileMenuOpen = !mobileMenuOpen"
                :aria-expanded="mobileMenuOpen"
                :aria-label="t('layout.a11y.toggleMenu')"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path v-if="!mobileMenuOpen" d="M3 12h18M3 6h18M3 18h18"/><path v-else d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
        </header>
        <!-- Mobile menu -->
        <nav v-if="mobileMenuOpen" :aria-label="t('layout.a11y.mobileNav')" class="flex flex-col px-5 py-4 gap-3 bg-background border-b-[0.5px] border-border text-[15px] lg:!hidden" @click="mobileMenuOpen = false">
            <Link v-for="link in navLinks" :key="link.key" :href="link.href" class="text-muted-foreground no-underline py-2">{{ t(link.key) }}</Link>
            <template v-if="!isAuthenticated">
                <Link v-if="isAppDomain" :href="login()" class="text-muted-foreground no-underline py-2">{{ t('layout.auth.login') }}</Link>
                <a v-else :href="appUrl(login())" class="text-muted-foreground no-underline py-2">{{ t('layout.auth.login') }}</a>
            </template>
            <div v-if="visibleLocales.length > 1" role="group" :aria-label="t('layout.a11y.languageSwitcher')" class="flex border-[0.5px] border-border rounded-full p-0.5 self-start mt-1" @click.stop>
                <a
                    v-for="loc in visibleLocales"
                    :key="loc"
                    :href="urlForLocale(currentPath(), loc)"
                    :aria-current="locale === loc ? 'true' : undefined"
                    :lang="loc"
                    class="rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-[0.6px] uppercase no-underline"
                    :class="locale === loc ? 'bg-foreground text-background' : 'bg-transparent text-muted-foreground'"
                >{{ loc.toUpperCase() }}</a>
            </div>
        </nav>

        <main id="main-content" class="flex-1">
            <slot />
        </main>

        <!-- Footer -->
        <footer class="px-5 md:px-10 lg:px-14 pt-16 pb-10" :aria-label="t('layout.a11y.footer')">
            <div class="grid grid-cols-1 gap-8 md:grid-cols-[1.4fr_repeat(2,1fr)] lg:grid-cols-[1.4fr_repeat(4,1fr)] mb-12">
                <div>
                    <Link :href="home()" class="inline-flex items-center gap-2 font-semibold text-[19px] tracking-[-0.5px] no-underline text-inherit">
                        <AppLogoIcon class="w-[22px] h-[22px]" aria-hidden="true" />
                        Novantt
                    </Link>
                    <p class="text-[13px] text-muted-foreground leading-[1.6] mt-4 max-w-[280px]">{{ t('layout.footer.tagline') }}</p>
                    <div class="flex gap-2 mt-[18px] flex-wrap">
                        <span class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-muted text-xs font-medium">
                            <img src="/images/mollie-logo.png" alt="Mollie" width="14" height="14" />
                            Connected with Mollie
                        </span>
                        <span class="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-muted text-xs font-medium">
                            <img src="/images/stripe-logo.svg" alt="Stripe" width="34" height="14" class="h-3.5 w-auto" />
                            Connected with Stripe
                        </span>
                    </div>
                    <div class="mt-3 rounded-md overflow-hidden inline-block">
                        <Wcag22AaBadge />
                    </div>
                </div>
                <div v-for="col in footerCols" :key="col.hKey">
                    <div class="font-jetbrains text-[10px] tracking-[1px] text-muted-foreground uppercase mb-3.5">{{ t(col.hKey) }}</div>
                    <template v-for="l in col.l" :key="l.tKey">
                        <a v-if="l.external" :href="l.href" target="_blank" rel="noopener noreferrer" class="flex items-center gap-1 text-[13px] no-underline text-foreground mb-2">{{ t(l.tKey) }}<svg width="11" height="11" viewBox="0 0 12 12" fill="none" style="opacity: 0.4;" aria-hidden="true"><path d="M3.5 1.5H10.5V8.5" stroke="currentColor" stroke-width="1.2"/><path d="M10.5 1.5L1.5 10.5" stroke="currentColor" stroke-width="1.2"/></svg><span class="sr-only">({{ t('layout.a11y.opensNewWindow') }})</span></a>
                        <Link v-else :href="l.href" class="block text-[13px] no-underline text-foreground mb-2">{{ t(l.tKey) }}</Link>
                    </template>
                </div>
            </div>
            <div v-if="page.props.landingPages?.length" class="border-t-[0.5px] border-border pt-6 mb-10">
                <div class="font-jetbrains text-[10px] tracking-[1px] text-muted-foreground uppercase mb-3.5">{{ t('layout.footer.columns.pages.heading') }}</div>
                <div class="flex flex-wrap gap-x-6 gap-y-2">
                    <Link v-for="lp in page.props.landingPages" :key="lp.slug" :href="`/p/${lp.slug}`" class="text-[13px] no-underline text-foreground">{{ lp.title }}</Link>
                </div>
            </div>
            <div class="border-t-[0.5px] border-border pt-6 font-jetbrains text-[10px] lg:text-[11px] text-muted-foreground tracking-[0.4px] leading-[1.6]">&copy; {{ new Date().getFullYear() }} Novantt</div>
        </footer>

        <CookieConsentBanner />
    </div>
</template>
